import { isRunStateActivelyExecuting, normalizeRunStateValue } from '@/lib/pieveRunState.js';
import getThreadId from '@/lib/threads/getThreadId.js';
import getThreadParentId from '@/lib/threads/getThreadParentId.js';
import getThreadProjectId from '@/lib/threads/getThreadProjectId.js';
import normalizeThreadAutomationActive from '@/lib/threads/normalizeThreadAutomationActive.js';
import normalizeThreadPinned from '@/lib/threads/normalizeThreadPinned.js';
import normalizeThreadType from '@/lib/threads/normalizeThreadType.js';

const HOME_PROJECT_ID = 'project-home';
const EMPTY_THREAD_ITEMS = [];

const hasUnopenedAssistantResponse = ({ latestAssistantResponseAt, lastOpenedAt }) => {
  const latestAssistantResponseTimestamp = latestAssistantResponseAt ? new Date(latestAssistantResponseAt).getTime() : NaN;
  const lastOpenedTimestamp = lastOpenedAt ? new Date(lastOpenedAt).getTime() : NaN;

  if (!Number.isFinite(latestAssistantResponseTimestamp)) {
    return false;
  }

  if (!Number.isFinite(lastOpenedTimestamp)) {
    return false;
  }

  return latestAssistantResponseTimestamp > lastOpenedTimestamp;
};

const buildThreadItems = ({ threads, projectMetaById, automationActiveByThreadId = {}, pendingInteractionThreadIdSet = null, sortBy = 'updated', currentUserId = null }) => {
  if (!Array.isArray(threads) || threads.length < 1) {
    return EMPTY_THREAD_ITEMS;
  }

  const hasKnownProjects = Object.keys(projectMetaById || {}).length > 0;
  const nextItems = threads
    .map((thread) => {
      const threadId = getThreadId(thread);

      if (!threadId) {
        return null;
      }

      const projectId = getThreadProjectId(thread);
      const isUnknownArchivedProject = hasKnownProjects && Boolean(projectId) && projectId !== HOME_PROJECT_ID && !projectMetaById?.[projectId];

      if (isUnknownArchivedProject) {
        return null;
      }

      const createdAt = thread?.createdAt || null;
      const updatedAt = thread?.updatedAt || null;
      const ageTimestamp = sortBy === 'created' ? createdAt : updatedAt;
      const isCompany = thread?.isCompany === true;
      const ownerUserId = String(thread?.userId || '').trim();
      const viewerUserId = String(currentUserId || '').trim();
      const canArchive = isCompany ? Boolean(ownerUserId && viewerUserId && ownerUserId === viewerUserId) : true;

      return {
        id: threadId,
        label: thread.title,
        ageTimestamp,
        createdAt,
        updatedAt,
        hasUnopenedAssistantResponse: hasUnopenedAssistantResponse({
          latestAssistantResponseAt: thread?.latestAssistantResponseAt || null,
          lastOpenedAt: thread?.lastOpenedAt || null
        }),
        hasPendingInteraction: pendingInteractionThreadIdSet instanceof Set ? pendingInteractionThreadIdSet.has(threadId) : false,
        threadType: normalizeThreadType(thread?.threadType || thread?.thread_type, 'default'),
        automationActive: Object.prototype.hasOwnProperty.call(automationActiveByThreadId || {}, threadId) ? automationActiveByThreadId?.[threadId] === true : normalizeThreadAutomationActive(thread),
        inProgress: isRunStateActivelyExecuting(thread?.runState?.state),
        awaitingApproval: normalizeRunStateValue(thread?.runState?.state) === 'awaiting_approval',
        needsUserInput: normalizeRunStateValue(thread?.runState?.state) === 'complete' && thread?.runState?.meta?.completionOutcome === 'needs_user',
        isCompany,
        canArchive,
        pinned: normalizeThreadPinned(thread),
        parentThreadId: getThreadParentId(thread),
        projectId,
        projectLabel: projectId ? projectMetaById?.[projectId]?.name || null : null
      };
    })
    .filter(Boolean);

  return nextItems.length > 0 ? nextItems : EMPTY_THREAD_ITEMS;
};

export default buildThreadItems;
